import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@edusense/shared-auth-core';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import type { AdminApplicationId } from '../apps/adminApplications';

interface AssignmentStat {
    reason: string;
    issued: number;
    redeemed: number;
    outstanding: number;
    expiredUnused: number;
    expiringSoon: number;
    /** Percentage, one decimal. */
    redemptionRate: number;
}

/** Below this, a batch is being ignored rather than merely slow to convert. */
const POOR_REDEMPTION_RATE = 20;

/**
 * Issued-versus-redeemed per batch.
 *
 * Issuance says nothing on its own — handing out coupons nobody spends is the main way this
 * mechanism fails, and it fails silently because the issuance itself always looks successful.
 * The redemption rate is the number that decides whether a batch was worth sending, and grouping
 * by reason separates a campaign being ignored from one particular batch being ignored.
 */
export function CouponAssignmentStats({ applicationId }: { applicationId: AdminApplicationId }) {
    const { token: accessToken } = useAuth();
    const [stats, setStats] = useState<AssignmentStat[]>([]);
    const [loading, setLoading] = useState(true);

    const getAuthHeaders = useCallback(() => {
        const headers: Record<string, string> = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
        const xsrfCookie = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
        if (xsrfCookie) {
            headers['X-XSRF-TOKEN'] = xsrfCookie.split('=')[1];
        }
        return headers;
    }, [accessToken]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        void (async () => {
            try {
                const response = await axios.get<{ stats: AssignmentStat[] }>(
                    '/api/v1/admin/coupons/assignments/stats',
                    { params: { applicationId }, headers: getAuthHeaders(), withCredentials: true },
                );
                if (!cancelled) setStats(response.data.stats ?? []);
            } catch {
                if (!cancelled) setStats([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [applicationId, getAuthHeaders]);

    if (loading) {
        return <p className="text-sm text-muted-foreground">지급 현황 불러오는 중…</p>;
    }

    if (stats.length === 0) {
        return <p className="text-sm text-muted-foreground">아직 지급된 쿠폰이 없습니다.</p>;
    }

    return (
        <section className="space-y-2">
            <div>
                <h2 className="text-lg font-semibold">지급 현황</h2>
                <p className="text-sm text-muted-foreground">
                    발급만으로는 아무것도 알 수 없습니다. 사용률이 판단 근거입니다.
                </p>
            </div>

            <Table mobileCards>
                <TableHeader>
                    <TableRow>
                        <TableHead>사유</TableHead>
                        <TableHead className="text-right">지급</TableHead>
                        <TableHead className="text-right">사용</TableHead>
                        <TableHead className="text-right">사용률</TableHead>
                        <TableHead className="text-right">미사용</TableHead>
                        <TableHead className="text-right">만료(미사용)</TableHead>
                        <TableHead className="text-right">만료 임박</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {stats.map((row) => (
                        <TableRow key={row.reason}>
                            <TableCell className="font-medium">{row.reason}</TableCell>
                            <TableCell className="text-right">{row.issued}</TableCell>
                            <TableCell className="text-right">{row.redeemed}</TableCell>
                            <TableCell className="text-right">
                                <span className={row.redemptionRate < POOR_REDEMPTION_RATE ? 'text-rose-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                                    {row.redemptionRate}%
                                </span>
                            </TableCell>
                            <TableCell className="text-right">{row.outstanding}</TableCell>
                            <TableCell className="text-right">
                                {/*
                                  Expired-unused is the clearest failure signal: those users were
                                  given something, told about it, and it still ran out.
                                */}
                                {row.expiredUnused > 0
                                    ? <span className="text-rose-600">{row.expiredUnused}</span>
                                    : row.expiredUnused}
                            </TableCell>
                            <TableCell className="text-right">
                                {row.expiringSoon > 0
                                    ? <Badge variant="secondary">{row.expiringSoon}</Badge>
                                    : row.expiringSoon}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </section>
    );
}

export default CouponAssignmentStats;
